import type { VirtualItem } from '@tanstack/react-virtual';

import { cn } from '@repo/lib/cn';
import { useVirtualizer } from '@tanstack/react-virtual';
import { CheckIcon, ChevronsUpDown, PhoneIcon } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button, ButtonGroup } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import type { CountrySelectOptionProps, FlagProps, ParsedCountry, PhoneNumberInputProps } from '.';

import { usePhoneField } from '.';

export function Flag(flagProps: FlagProps) {
  const { countryCode, className, ...props } = flagProps;
  const [loaded, setLoaded] = useState<boolean>(false);
  const cdnUrl: string = 'https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/';
  const flagUrl: string = `${cdnUrl}${countryCode.toLowerCase()}.svg`;

  return (
    <span className={cn('bg-accent relative flex h-3.5 w-4.5 overflow-hidden rounded-xs [&_svg]:size-full [&_svg]:grow', className)} {...props}>
      <span className={cn('text-muted-foreground absolute top-1/2 left-1/2 hidden -translate-x-1/2 -translate-y-1/2 text-[8px] font-medium', !loaded && 'block animate-pulse')}>
        {countryCode.toUpperCase()}
      </span>
      <img src={flagUrl} alt={`${countryCode} flag`} onLoad={() => setLoaded(true)} className={cn(!loaded && 'invisible')} />
    </span>
  );
}

export function CountrySelectOption(countrySelectOptionProps: CountrySelectOptionProps) {
  const { country, isActive, onSelect, className, ...props } = countrySelectOptionProps;
  return (
    <CommandItem className={cn('gap-2 text-start whitespace-normal', className)} onSelect={() => onSelect(country.iso2)} {...props}>
      <Flag countryCode={country.iso2.toUpperCase()} aria-label={country.name} />
      <span className="text-foreground flex-1 text-sm">{country.name}</span>
      <span className="text-muted-foreground text-sm">{`+${country.dialCode}`}</span>
      <CheckIcon className={`ml-auto size-4 ${isActive ? 'opacity-100' : 'opacity-0'}`} />
    </CommandItem>
  );
}

export function PhoneNumberInput(phoneNumberInputProps: PhoneNumberInputProps) {
  const { disabled, value, onChange, placeholder, disableDialCodePrefill = true, ...props } = phoneNumberInputProps;
  const parentRef = useRef<HTMLDivElement>(null);
  const { inputProps, country, countries, selectCountry, search, setSearch } = usePhoneField({
    ...props,
    disableDialCodePrefill: disableDialCodePrefill,
    value,
    onChange: (value) => {
      onChange?.(value.phone);
    },
  });

  const virtualizer = useVirtualizer({
    count: countries.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 15,
  });

  const virtualOptions: VirtualItem[] = virtualizer.getVirtualItems();

  const handlePopoverOpenChange = (open: boolean) => {
    if (open) {
      requestAnimationFrame(() => {
        virtualizer.measure();
      });
    }
  };

  const showFlag: boolean = typeof inputProps.value === 'string' && /\d/.test(inputProps.value.replace(/\s/g, ''));

  return (
    <ButtonGroup className="w-full">
      <Popover onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" disabled={disabled}>
            {showFlag ? (
              <Flag countryCode={country.iso2.toUpperCase()} aria-label={country.name} />
            ) : (
              <span className="w-4.5">
                <PhoneIcon className="text-muted-foreground size-3.5" />
              </span>
            )}

            <ChevronsUpDown className={'-mr-2 size-4 opacity-50'} />
          </Button>
        </PopoverTrigger>
        <PopoverContent disablePortal={true} align={'start'} className={'w-74 overflow-hidden border p-0'}>
          <Command shouldFilter={false}>
            <CommandInput placeholder={'Wyszukaj kraj...'} value={search} onValueChange={setSearch} />
            <CommandList>
              {countries.length === 0 && search && <CommandEmpty>Nie znaleziono kraju.</CommandEmpty>}

              <CommandGroup ref={parentRef} className="overflow-auto">
                <div
                  style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {virtualOptions.map((virtualItem) => {
                    const c: ParsedCountry | undefined = countries[virtualItem.index];
                    if (!c) return null;
                    return (
                      <CountrySelectOption
                        key={c.iso2}
                        country={c}
                        isActive={c.iso2 === country.iso2}
                        onSelect={selectCountry}
                        className="absolute left-0 flex w-full cursor-pointer items-center gap-2"
                        style={{
                          width: '100%',
                          height: `${virtualItem.size}px`,
                          transform: `translateY(${virtualItem.start}px)`,
                        }}
                      />
                    );
                  })}
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input {...inputProps} placeholder={placeholder} />
    </ButtonGroup>
  );
}
